import { useState, useCallback } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function validateLeadForm(form) {
  if (!form.first_name.trim()) return 'Le prénom est obligatoire.';
  if (!form.phone.trim()) return 'Le téléphone est obligatoire.';
  return null;
}

export default function LeadForm({ fiduciarySlug, city }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', email: '',
    city: city || '', declaration_type: 'Personne physique', message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const updateField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const validationError = validateLeadForm(form);
    if (validationError) { setError(validationError); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/api/leads/submit`, { ...form, fiduciary_slug: fiduciarySlug || '' });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  }, [form, fiduciarySlug]);

  if (success) {
    return (
      <div className="border border-[#16A34A] bg-green-50 p-8 text-center" data-testid="lead-form-success">
        <CheckCircle className="mx-auto mb-4 text-[#16A34A]" size={48} />
        <h3 className="font-['Chivo'] font-bold text-2xl text-[#111827] mb-2">Demande envoyée</h3>
        <p className="text-gray-600 font-['IBM_Plex_Sans']">Un expert fiscal de votre région vous contactera dans les plus brefs délais.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="block text-sm font-bold text-gray-900 mb-2">Prénom *</Label>
          <Input data-testid="lead-first-name" value={form.first_name} onChange={(e) => updateField('first_name', e.target.value)} placeholder="Jean" className="bg-white border border-gray-300 rounded-none px-4 py-3 focus:border-[#E3000F] focus:ring-1 focus:ring-[#E3000F] text-base" />
        </div>
        <div>
          <Label className="block text-sm font-bold text-gray-900 mb-2">Nom</Label>
          <Input data-testid="lead-last-name" value={form.last_name} onChange={(e) => updateField('last_name', e.target.value)} placeholder="Dupont" className="bg-white border border-gray-300 rounded-none px-4 py-3 focus:border-[#E3000F] focus:ring-1 focus:ring-[#E3000F] text-base" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="block text-sm font-bold text-gray-900 mb-2">Téléphone *</Label>
          <Input data-testid="lead-phone" type="tel" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} placeholder="+41 79 123 45 67" className="bg-white border border-gray-300 rounded-none px-4 py-3 focus:border-[#E3000F] focus:ring-1 focus:ring-[#E3000F] text-base" />
        </div>
        <div>
          <Label className="block text-sm font-bold text-gray-900 mb-2">Email</Label>
          <Input data-testid="lead-email" type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} placeholder="jean@exemple.ch" className="bg-white border border-gray-300 rounded-none px-4 py-3 focus:border-[#E3000F] focus:ring-1 focus:ring-[#E3000F] text-base" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="block text-sm font-bold text-gray-900 mb-2">Canton / Ville</Label>
          <Select value={form.city} onValueChange={(v) => updateField('city', v)}>
            <SelectTrigger data-testid="lead-city" className="bg-white border border-gray-300 rounded-none px-4 py-3 focus:border-[#E3000F] focus:ring-1 focus:ring-[#E3000F] text-base h-auto">
              <SelectValue placeholder="Sélectionnez votre ville" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Genève">Genève</SelectItem>
              <SelectItem value="Lausanne">Lausanne</SelectItem>
              <SelectItem value="Fribourg">Fribourg</SelectItem>
              <SelectItem value="Neuchâtel">Neuchâtel</SelectItem>
              <SelectItem value="Sion">Sion</SelectItem>
              <SelectItem value="Montreux">Montreux</SelectItem>
              <SelectItem value="Nyon">Nyon</SelectItem>
              <SelectItem value="Yverdon">Yverdon-les-Bains</SelectItem>
              <SelectItem value="Autre">Autre</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="block text-sm font-bold text-gray-900 mb-2">Type de déclaration</Label>
          <Select value={form.declaration_type} onValueChange={(v) => updateField('declaration_type', v)}>
            <SelectTrigger data-testid="lead-type" className="bg-white border border-gray-300 rounded-none px-4 py-3 focus:border-[#E3000F] focus:ring-1 focus:ring-[#E3000F] text-base h-auto">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Personne physique">Personne physique</SelectItem>
              <SelectItem value="Personne morale">Personne morale</SelectItem>
              <SelectItem value="Indépendant">Indépendant</SelectItem>
              <SelectItem value="Frontalier">Frontalier</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="block text-sm font-bold text-gray-900 mb-2">Message (optionnel)</Label>
        <Textarea data-testid="lead-message" value={form.message} onChange={(e) => updateField('message', e.target.value)} placeholder="Décrivez brièvement votre situation fiscale..." className="bg-white border border-gray-300 rounded-none px-4 py-3 focus:border-[#E3000F] focus:ring-1 focus:ring-[#E3000F] text-base min-h-[100px]" />
      </div>
      {error && <p className="text-[#E3000F] text-sm font-semibold" data-testid="lead-form-error">{error}</p>}
      <button type="submit" disabled={loading} className="w-full bg-[#E3000F] text-white hover:bg-[#A6000B] px-8 py-4 font-bold text-lg tracking-wide transition-colors disabled:opacity-50 font-['IBM_Plex_Sans'] flex items-center justify-center gap-2" data-testid="lead-submit-button">
        {loading && <Loader2 className="animate-spin" size={20} />}
        {loading ? 'Envoi en cours...' : 'Obtenir mon devis gratuit'}
      </button>
      <p className="text-xs text-gray-500 text-center">Service gratuit et sans engagement. Vos données sont protégées conformément à la LPD suisse.</p>
    </form>
  );
}
